/**
 * cniViewerSession — remembers which live broadcast (state + LGA) the CNI
 * news viewer was watching, so a browser refresh resumes that broadcast
 * instead of dropping the viewer back on the state/LGA picker.
 *
 * The viewer page (NewsDetail) already restores its selection from
 * sessionStorage's `stateId`/`provinceId`. That works only while the tab's
 * session storage survives and the refresh actually lands back on the viewer
 * route. Two production cases break it:
 *
 *   1. An expired login token makes App.js wipe local+session storage on
 *      mount, so the selection is gone before the page can restore it.
 *   2. A refresh that gets bounced back to the CM landing page ("/") never
 *      mounts the viewer route at all, so the intro + picker plays again.
 *
 * These keys are an ADDITIVE mirror in localStorage under names nothing else
 * in the app uses — the existing `stateId`/`provinceId` keys (including
 * Broadcaster's own localStorage copies) keep their current meaning and
 * behavior untouched.
 */

export const CNI_STATE_KEY = 'cniViewerState';
export const CNI_LGA_KEY = 'cniViewerLga';
// Set while the viewer page is mounted, cleared when it unmounts. React runs
// that cleanup on an in-app navigation away (user went home / picked another
// screen) but NOT on a browser refresh — which is exactly how "the page
// reloaded while I was watching" is told apart from "I deliberately left".
export const CNI_WATCHING_KEY = 'cniViewerWatching';

// Private-mode / storage-disabled browsers throw on access; the viewer must
// still work, just without the resume, so every access is guarded.
const read = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const write = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable — resume simply won't be possible */
  }
};

export function saveCniSelection(stateName, lgaName) {
  if (!stateName || !lgaName) return;
  write(CNI_STATE_KEY, stateName);
  write(CNI_LGA_KEY, lgaName);
}

export function readCniSelection() {
  return { state: read(CNI_STATE_KEY), lga: read(CNI_LGA_KEY) };
}

export function markCniWatching(isWatching) {
  // The floating CNI PiP loads the viewer route inside an iframe; that must
  // not count as "the user is watching on this page", or closing the PiP
  // (which destroys the frame without running React cleanup) would leave the
  // flag set forever.
  try {
    if (window.top !== window.self) return;
  } catch {
    return; // cross-origin frame — treat as not top-level
  }
  write(CNI_WATCHING_KEY, isWatching ? '1' : '');
}

export function wasCniWatching() {
  return read(CNI_WATCHING_KEY) === '1';
}

/** A state/LGA the viewer page can restore itself from, from either store. */
export function hasCniSelection() {
  try {
    if (sessionStorage.getItem('stateId') && sessionStorage.getItem('provinceId')) {
      return true;
    }
  } catch {
    /* storage unavailable */
  }
  const { state, lga } = readCniSelection();
  return Boolean(state && lga);
}

/**
 * True only when the current page load came from a browser refresh (F5 /
 * reload button), including one that was redirected on the way. A typed URL,
 * a bookmark or an in-app route change is never a reload, so resume logic
 * gated on this can't hijack a deliberate visit to the landing page.
 */
export function isReloadNavigation() {
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav?.type) return nav.type === 'reload';
    // Legacy fallback for older browsers/Smart TV webviews.
    return performance.navigation?.type === 1;
  } catch {
    return false;
  }
}
